import { RefObject, useLayoutEffect, useState } from 'react';
import { TITLE_MEASURE_ATTRIBUTE } from '../types';

export type TitleFit = 'inline' | 'clamped';
export type BadgesFit = 'fit' | 'scroll';

export interface TitleRowLayout {
  titleFit?: TitleFit;
  badgesFit?: BadgesFit;
}

export interface UseTitleRowLayoutParams {
  containerRef: RefObject<HTMLElement | null>;
  titleRef: RefObject<HTMLElement | null>;
  titleActionRef: RefObject<HTMLElement | null>;
  titleMetaRef: RefObject<HTMLElement | null>;
  titleMetaPinnedRef: RefObject<HTMLElement | null>;
}

const WIDE_CONTENT_RATIO = 0.5;

const INACTIVE: TitleRowLayout = {};

const getIntrinsicWidth = (element: Element | null): number =>
  element instanceof HTMLElement ? element.offsetWidth : 0;

/**
 * Splits the banner title row into the four name/badges layout rules by comparing each side's
 * intrinsic width against half of the row's available width.
 *
 * | name  | badges | titleFit  | badgesFit |
 * | ----- | ------ | --------- | --------- |
 * | <=50% | <=50%  | `inline`  | `fit`     |
 * | <=50% | >50%   | `inline`  | `scroll`  |
 * | >50%  | <=50%  | `clamped` | `fit`     |
 * | >50%  | >50%   | `clamped` | `scroll`  |
 *
 * Returns an empty layout (rules disabled) unless both the title and the title-meta slot are filled.
 */
export const useTitleRowLayout = ({
  containerRef,
  titleRef,
  titleActionRef,
  titleMetaRef,
  titleMetaPinnedRef,
}: UseTitleRowLayoutParams): TitleRowLayout => {
  const [layout, setLayout] = useState<TitleRowLayout>(INACTIVE);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const title = titleRef.current;
    const titleMeta = titleMetaRef.current;
    if (!container || !title || !titleMeta) return undefined;

    let frameId = 0;

    const measure = () => {
      // Both widths come from layout-independent nodes (an unwrapped clone of the name and a
      // max-content badges track), so applying the result can never feed back into the inputs.
      const nameNode = title.querySelector(`[${TITLE_MEASURE_ATTRIBUTE}]`);
      const badgesTrack = titleMeta.firstElementChild;

      if (!nameNode || !badgesTrack) {
        setLayout(previous => (previous === INACTIVE ? previous : INACTIVE));
        return;
      }

      const reserved = getIntrinsicWidth(titleActionRef.current) + getIntrinsicWidth(titleMetaPinnedRef.current);
      const available = container.clientWidth - reserved;
      if (available <= 0) return;

      const threshold = available * WIDE_CONTENT_RATIO;
      const titleFit: TitleFit = getIntrinsicWidth(nameNode) > threshold ? 'clamped' : 'inline';
      const badgesFit: BadgesFit = getIntrinsicWidth(badgesTrack) > threshold ? 'scroll' : 'fit';

      setLayout(previous =>
        previous.titleFit === titleFit && previous.badgesFit === badgesFit ? previous : { titleFit, badgesFit },
      );
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(measure);
    };

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    resizeObserver.observe(container);
    resizeObserver.observe(title);
    resizeObserver.observe(titleMeta);

    // Slot content is portalled in after mount and the badges resolve from async queries.
    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(container, { childList: true, subtree: true, characterData: true });

    measure();

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [containerRef, titleRef, titleActionRef, titleMetaRef, titleMetaPinnedRef]);

  return layout;
};
